import CaseStudySliderItemCard from "@/components/CaseStudiesSlider/CaseStudySliderItemCard";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import CarouselArrow from "@/assets/svg/carouselArrow.svg";
import { motion } from "motion/react";
import AnimatedCounter from "@/components/Shared/AnimatedCounter";
import mq from "@/utils/mq";

interface CaseStudiesCarouselProps {
  caseStudies: any[];
}

export type CarouselApi = UseEmblaCarouselType[1];

const CarouselWrapper = styled.div`
  overflow: hidden;
  position: relative;
  margin-top: 86px;
  ${mq.mobile(css`
    display: flex;
    flex-direction: column-reverse;
    margin-top: 16px;
  `)};
`;

const EmblaSlide = styled.div`
  height: 485px;
  width: auto;
  aspect-ratio: 3/4;
  margin-right: 24px;
  ${mq.mobile(css`
    margin-right: 8px;
    aspect-ratio: 3/4;
    height: 385px;
  `)};
`;

const CarouselContainer = styled.div`
  display: flex;
  margin-left: calc((100% - (3 * 30%) - (2 * 24px)) / 2);
  margin-right: calc((100% - (3 * 30%) - (2 * 24px)) / 2);
  margin-bottom: 32px;

  &:first-child {
  }

  ${mq.mobile(css`
    margin-left: 8px;
    margin-right: 8px;
    margin-bottom: 16px;
  `)};
`;

const CaseStudySlide = styled.div`
  flex: 0 0 30%;
  min-width: 0;
  padding-left: 12px;
  padding-right: 12px;
  margin-right: 0;

  ${mq.mobile(css`
    flex: 0 0 80%;
    padding-left: 4px;
    padding-right: 4px;
  `)};
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 32px;
  width: 100%;
  padding-right: 36px;
  gap: 16px;
  ${mq.mobile(css`
    padding-right: 16px;
    padding-left: 16px;
    margin-bottom: 16px;
  `)};
`;

const ProgressContainer = styled.div`
  max-width: 300px;
  width: 100%;
  height: 2px;
  background-color: #23252a;
`;

const ProgressBar = styled(motion.div)`
  height: 2px;
  background-color: #d0d6e0;
`;

const ControlButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 2px;
`;

const ControlButton = styled.button<{ $flipped?: boolean }>`
  width: 18px;
  height: 18px;
  display: flex;
  border: none;
  background: none;
  outline: none;
  margin: 0;
  padding: 0;
  justify-content: center;
  align-items: center;
  color: #677076;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  transform: ${({ $flipped }) => ($flipped ? "rotate(180deg)" : "rotate(0deg)")};
  &:hover {
    color: #fff;
    transition: all 0.3s ease-in-out;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const AnimatedNumberSpan = styled.span`
  color: #ffffff;
`;

const SlideIndicatorLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #677076;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  user-select: none;
  min-width: 40px;
  text-align: right;
`;

export default function CaseStudiesCarousel({ caseStudies }: CaseStudiesCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",

    duration: 25,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [completedPercentage, setCompletedPercentage] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(
    (api: CarouselApi) => {
      if (!api) {
        return;
      }
      setActiveSlide(api.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());

      const totalSlides = caseStudies?.length || 0;
      if (totalSlides <= 1) {
        setCompletedPercentage(100);
        return;
      }
      const percentage = Math.round((api.selectedScrollSnap() / (totalSlides - 1)) * 100);
      setCompletedPercentage(Math.max(0, Math.min(100, percentage)));
    },
    [caseStudies],
  );

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);

    return () => {
      emblaApi?.off("select", onSelect);
      emblaApi?.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const formattedTotalSlides = (caseStudies?.length || 0).toString().padStart(2, "0");

  return (
    <CarouselWrapper ref={emblaRef}>
      <CarouselContainer>
        {caseStudies &&
          caseStudies.map((study, index) => (
            <EmblaSlide key={study?._uid || index}>
              <CaseStudySliderItemCard caseStudy={study} />
            </EmblaSlide>
          ))}
      </CarouselContainer>
      <ControlsContainer>
        <ProgressContainer>
          <ProgressBar
            initial={{ width: "0%" }}
            animate={{ width: `${completedPercentage}%` }}
            transition={{ duration: 0.5, ease: [0.77, 0, 0.175, 1] }}
          />
        </ProgressContainer>
        <SlideIndicatorLabel>
          <AnimatedNumberSpan>
            <AnimatedCounter value={activeSlide + 1} />
          </AnimatedNumberSpan>
          &nbsp;/&nbsp;{formattedTotalSlides}
        </SlideIndicatorLabel>
        <ControlButtons>
          <ControlButton $flipped={true} onClick={scrollPrev} aria-label='Previous slide'>
            <CarouselArrow />
          </ControlButton>
          <ControlButton onClick={scrollNext} aria-label='Next slide'>
            <CarouselArrow />
          </ControlButton>
        </ControlButtons>
      </ControlsContainer>
    </CarouselWrapper>
  );
}
